import React, { useState, useEffect, useCallback } from 'react';
import EditStudent from '../../AddsEdits/Student/EditStudent.js';
import { SERVER_URL } from '../../../constants.js'
import { formatLocalDateOnly } from '../../shared utils/dateFormatters.js'
import { toast, ToastContainer } from 'react-toastify';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useToast } from '../../shared utils/useToast.js';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';



function StudentDetail(props) {
  const { selectedPerson, selectedStay, setSelectedStay, showFinancials, onClose } = props;
  const [student, setStudent] = useState(null);
  const { showToast, toastProps } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const REQUIRED_PHRASE = 'delete this student';

  const handleDeleteStudent = async () => {
    setDeleting(true);
    try {
      const token = sessionStorage.getItem('bearer');
      const res = await fetch(`${SERVER_URL}api/students/${selectedPerson.id}/full-delete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete student');
      setDeleteDialogOpen(false);
      showToast('Student deleted successfully.', 'success');
      setTimeout(() => onClose?.(), 1000);
    } catch (err) {
      console.error(err);
      showToast('Failed to delete student.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });
  
  const fetchStays = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/student/${selectedPerson.id}/stays`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch stays');
        }
      })
      .then((data) => {
        // Automatically select the most recent stay if none is currently selected
        if (!selectedStay && data.length > 0) {
          setSelectedStay(data[data.length - 1]);  // Set to the most recent stay
          //console.log(selectedStay);
        }
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id, selectedStay, setSelectedStay]);

  const fetchStudent = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/students/${selectedPerson.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return null;
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch student');
        }
      })
      .then((data) => {
        setStudent(data);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);

  useEffect(() => {
    fetchStudent();
    fetchStays();
  }, [fetchStudent, fetchStays]);

  let imgPath;
  if (student && student.studentGender && student.studentGender.toLowerCase() === 'male') {
    const maleImgPaths = [
      "/assets/img/1.png",
      "/assets/img/3.png",
      "/assets/img/6.png",
      "/assets/img/8.png"
    ];
    imgPath = process.env.PUBLIC_URL + maleImgPaths[Math.floor(Math.random() * maleImgPaths.length)];
  } else if (student) {
    const femaleImgPaths = [
      "/assets/img/2.png",
      "/assets/img/4.png",
      "/assets/img/5.png",
      "/assets/img/7.png"
    ];
    imgPath = process.env.PUBLIC_URL + femaleImgPaths[Math.floor(Math.random() * femaleImgPaths.length)];
  }

  const {
    id, 
    studentName, 
    studentSurname, 
    dateAdded, 
    mtRef, 
    studentDob, 
    studentGender, 
    studentNationality, 
    englishLevel, 
    roomRequirements, 
    classRequirements, 
    allergies, 
    notes, 
    hasPoolPermission,
    hasPhotoPermission,
    hasMedicalPermission,
    hasHospitalPermission,
    hasExcursionPermission,
    hasActivityPermission,
    hasSupervisionPermission,
    emergencyContact,
    mobilePhoneOptOut,
    mobileNumber,
    tshirtSize,
  } = student || {};

  const editStudent = (student, id) => {
    const token = sessionStorage.getItem("bearer"); 
    const formattedStudent = {
      ...student,
      studentDob: formatLocalDateOnly(student.studentDob)
    };
    fetch(`${SERVER_URL}api/students/${id}`, {
      method: 'PUT', 
      headers: {
        'Content-Type':'application/json',
        'Authorization' : `Bearer ${token}`
      },
      body: JSON.stringify(formattedStudent)
    })
    .then(response => {
      if (response.ok) {
        showToast('Student updated successfully', 'success');
        fetchStudent();
      }
      else {
        showToast('Error updating student', 'error');
      }
    })
    .catch(err => {
    console.error(err);
    showToast('Network error or server unreachable.', 'error');
  });
  }

  return (
    <React.Fragment>
      <div className="detail-card" style={{ padding: '20px 0' }}>
        <table style={{ width: '80%', textAlign: 'left', margin: 'auto', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td rowSpan="2" style={{ textAlign: 'center' }}><img className="br-100 h3 w3 dib" alt={selectedPerson.id} src={imgPath} /></td>
              <td>DB ref: {id}</td>
              <td>Added: {dateAdded}</td>
              <td>Master Tracker: {mtRef}</td>
            </tr>
            <tr>
              <td>Nationality: {studentNationality}</td>
              <td>Dob: {formatLocalDateOnly(new Date(studentDob))}</td>
              <td>Gender: {studentGender}</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'center' }}>{studentName} {studentSurname}</td>
              <td>English level: {englishLevel}</td>
              <td>T-shirt size:</td>
              <td>{tshirtSize || 'N/A'}</td>
            </tr>
            <tr style={{ height: '50px' }}><td colSpan="4" > </td></tr>
            <tr><td>Room Requirements: </td><td colSpan="3">{roomRequirements}</td></tr>
            <tr><td>Class Requirements: </td><td colSpan="3">{classRequirements}</td></tr>
            <tr><td>Allergies: </td><td colSpan="3">{allergies}</td></tr>
            <tr><td>Notes: </td><td colSpan="3">{notes}</td></tr>
            <tr><td>Mobile Number: </td><td colSpan="3">{mobileNumber || 'N/A'}</td></tr>
            <tr><td>Emergency Contact: </td><td colSpan="3">{emergencyContact || 'N/A'}</td></tr>
            <tr><td>Mobile Phone Opt-Out: </td><td colSpan="3">{mobilePhoneOptOut ? 'Yes' : 'No'}</td></tr>
            <tr>
              <td colSpan="2">Permissions:</td>
              <td>Photo:</td>
              <td>{hasPhotoPermission ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>Medical:</td>
              <td>{hasMedicalPermission ? 'Yes' : 'No'}</td>
              <td>Hospital:</td>
              <td>{hasHospitalPermission ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>Excursion:</td>
              <td>{hasExcursionPermission ? 'Yes' : 'No'}</td>
              <td>Activity:</td>
              <td>{hasActivityPermission ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>Supervision:</td>
              <td>{hasSupervisionPermission ? 'Yes' : 'No'}</td>
              <td>Pool:</td>
              <td>{hasPoolPermission ? 'Yes' : 'No'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <div style={{ color: '#999999', fontSize: '10px' }}>Student: {selectedPerson.id}
          <EditStudent passedStudent={student} editStudent={editStudent} />
        </div>
        <p style={{ color: '#999999', fontSize: '10px' }}>Selected Stay: {selectedStay ? selectedStay.stayId : 'None'}</p>
        <div style={{ color: '#999999', fontSize: '10px' }}>Is authenticated: {sessionStorage.getItem('isAuthenticated') ? 'Yes' : 'No'}</div>
      </div>
      {showFinancials && (
        <>
          <Box sx={{ mt: 3, p: 2, border: '1px solid #f44336', borderRadius: 1, bgcolor: '#ffebee', maxWidth: '80%', mx: 'auto' }}>
            <Typography variant="subtitle2" sx={{ color: '#b71c1c', mb: 1 }}>
              Danger Zone
            </Typography>
            <IconButton
              color="error"
              onClick={() => { setDeleteConfirmText(''); setDeleteDialogOpen(true); }}
              title="Delete this student"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#b71c1c', ml: 1 }}>
              Permanently delete this student and all associated data
            </Typography>
          </Box>

          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle sx={{ color: '#b71c1c' }}>Delete Student</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2 }}>
                This will permanently delete <strong>{student?.studentName} {student?.studentSurname}</strong> (ID: {selectedPerson.id}) and all their stays, transfers, bookings, and associated data.
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Type <strong>{REQUIRED_PHRASE}</strong> to confirm:
              </Typography>
              <TextField
                fullWidth
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={REQUIRED_PHRASE}
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleDeleteStudent}
                disabled={deleteConfirmText !== REQUIRED_PHRASE || deleting}
                startIcon={<DeleteIcon />}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar {...toastProps}>
  <Alert onClose={toastProps.onClose} severity={toastProps.severity}>
    {toastProps.message}
  </Alert>
</Snackbar>
    </React.Fragment>
  );
}

export default StudentDetail;
